import React, { useState, useRef } from "react";
import { theme } from "../../../../Themes/Themes";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import useStore from "../../../../Store/Store";
import DialogButton from "../../../Dialog/DialogButton";
import {
  generateCustomUsecase,
  getTopXReadmeFiles,
} from "../../../OnboardingWorkflow/OnboardingWorkflow";
import { processUsecaseDescription } from "../../../../utils/MarkdownProcessor";
import { logger } from "../../../../utils/Logger";

interface CustomUsecaseItemProps {}

/**
 * Custom usecase input component that allows users to enter their own usecase/feature
 * and has similar styling to UsecaseItem but with an input field.
 */
const CustomUsecaseItem: React.FC<CustomUsecaseItemProps> = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [keywordsValue, setKeywordsValue] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const keywordsRef = useRef<HTMLTextAreaElement>(null);

  const {
    currentRepo,
    usecaseFeatures,
    repoTree,
    simulations,
    setSimulations,
    setSelectedSimulationKey,
    setLoadingNotification,
    setErrorNotification,
  } = useStore((state) => ({
    currentRepo: state.currentRepo,
    usecaseFeatures: state.usecaseFeatures,
    repoTree: state.repoTree,
    simulations: state.simulationsState.simulations,
    setSimulations: state.setSimulations,
    setSelectedSimulationKey: state.setSelectedSimulationKey,
    setLoadingNotification: state.setLoadingNotification,
    setErrorNotification: state.setErrorNotification,
  }));

  const handleInputFocus = () => {
    setIsExpanded(true);
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    // Check if the blur event was caused by clicking the Go button or the other textarea
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (
      relatedTarget &&
      (relatedTarget.id === "custom-usecase-go-button" ||
        relatedTarget === keywordsRef.current ||
        relatedTarget === inputRef.current)
    ) {
      return; // Don't collapse if Go button or other textarea was clicked
    }

    if (!inputValue.trim() && !keywordsValue.trim()) {
      setIsExpanded(false);
    }
  };

  const handleGoClick = async () => {
    if (!inputValue.trim() || isGenerating) {
      return;
    }

    setIsGenerating(true);
    setLoadingNotification("Generating custom use case...");

    try {
      // Get README files
      const readmeFiles = await getTopXReadmeFiles(4);

      // Generate the custom use case using Gemini
      const customUsecase = await generateCustomUsecase(
        inputValue.trim(),
        keywordsValue.trim(),
        repoTree,
        readmeFiles
      );

      if (!customUsecase) {
        setErrorNotification(
          "Failed to generate custom use case. Please try again."
        );
        return;
      }

      // Create a unique simulation name (add timestamp to avoid conflicts)
      const timestamp = new Date()
        .toISOString()
        .slice(11, 19)
        .replace(/:/g, "-");
      const simulationName = `${customUsecase.usecaseTitle} (${timestamp})`;

      // Create the simulation object similar to how setUsecaseFeatures does it
      const simulation = {
        name: simulationName,
        simSteps: [], // Empty initially, will be generated when selected
        description: processUsecaseDescription(
          customUsecase.usecaseDescription
        ),
        simulationNodesAndEdges: {},
        isAIGenerated: true,
        keywords: customUsecase.usecaseKeywords,
      };

      // Add the simulation to the existing simulations
      const updatedSimulations = {
        ...simulations,
        [simulationName]: simulation,
      };

      // Update simulations in the store
      await setSimulations(updatedSimulations);

      // Clear the input fields
      setInputValue("");
      setKeywordsValue("");
      setIsExpanded(false);

      // Select the newly created simulation to trigger the simstep generation workflow
      await setSelectedSimulationKey(simulationName);

      logger.info(
        `CustomUsecaseItem: Successfully created and selected simulation: ${simulationName}`
      );

      setLoadingNotification("");
    } catch (error) {
      logger.error(
        "CustomUsecaseItem: Error generating custom use case:",
        error
      );
      setErrorNotification(
        "An error occurred while generating the custom use case."
      );
    } finally {
      setIsGenerating(false);
      setLoadingNotification("");
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && inputValue.trim() && !isGenerating) {
      e.preventDefault(); // Prevent new line on Enter
      handleGoClick();
    }
  };

  // Conditionally update placeholder based on whether usecases are rendered
  const placeholder =
    usecaseFeatures && usecaseFeatures.length > 0
      ? "Looking to understand a specific Use-case/feature of this codebase that's not mentioned below? Mention it here!"
      : "Looking to understand a specific Use-case/feature of this codebase? Mention it here!";

  const wrapperStyle: React.CSSProperties = {
    height: "fit-content",
    minHeight: "fit-content",
    overflow: "hidden",
    transition: "height 0.2s ease",
    padding: "0px 0px 0px 0px",
  };

  const textareaStyle: React.CSSProperties = {
    flex: 1,
    minHeight: "30px",
    borderRadius: "15px",
    paddingLeft: "15px",
    paddingRight: "15px",
    paddingTop: "8px",
    outline: "none",
    border: `1px solid ${theme.custom.darkestGrey}`,
    color: `${theme.custom.darkestGrey} !important`,
    background: "transparent",
    cursor: "text",
    fontSize: "13px",
    textAlign: "left",
    transition: "ease-in 0.1s",
    fontFamily: "Poppins-Medium",
    resize: "none",
    lineHeight: "1.4",
    caretColor: theme.custom.darkestGrey,
    opacity: "1 !important",
  };

  return (
    <div
      className={`SourceDocFile linked ${isExpanded ? "selectedFile" : ""}`}
      style={{
        cursor: "pointer",
        borderRadius: "15px",
        padding: "20px 15px 15px 15px",
        width: "calc(100% - 50px)",
        minHeight: "fit-content",
        transition: "all 0.2s ease",
        display: "block",
        marginBottom: "10px",
        opacity: isGenerating ? 0.7 : 1,
      }}
    >
      <div className="SourceDocFileInternalWrapper" style={wrapperStyle}>
        <div
          className="SourceDocFileInternalContent"
          style={{ marginBottom: 4, alignItems: "flex-start" }}
        >
          {/* Icon */}
          <div
            className="iconWrapper"
            style={{
              marginRight: 6,
              alignSelf: "flex-start",
              marginTop: "8px",
            }}
          >
            <AutoAwesomeIcon
              style={{
                width: "20px",
                height: "20px",
                marginRight: "8px",
                animation: isGenerating
                  ? "ai-pulse 2s ease-in-out infinite"
                  : "none",
              }}
            />
          </div>

          {/* Input fields with Go button */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              width: "calc(100% - 31px)",
              gap: "8px",
            }}
          >
            {/* Main input and Go button row */}
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "8px",
              }}
            >
              <textarea
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                onKeyPress={handleKeyPress}
                placeholder={placeholder}
                rows={3}
                style={textareaStyle}
                className="custom-usecase-input"
                disabled={isGenerating}
              />

              <div
                style={{
                  alignSelf: "center",
                  marginTop: "8px",
                }}
              >
                <DialogButton
                  onClick={handleGoClick}
                  id="custom-usecase-go-button"
                  text={isGenerating ? "..." : "Go"}
                  fontSize="12px"
                  buttonStyle={{
                    width: "30px",
                    height: "24px",
                    padding: "0 8px",
                    pointerEvents:
                      inputValue.trim() && !isGenerating ? "auto" : "none",
                    border: `1px solid ${theme.custom.darkestGrey}`,
                    color: `${theme.custom.darkestGrey} !important`,
                    background: "transparent",
                    cursor: isGenerating ? "wait" : "pointer",
                  }}
                />
              </div>
            </div>

            {/* Keywords input - only shown when expanded */}
            {isExpanded && (
              <textarea
                ref={keywordsRef}
                value={keywordsValue}
                onChange={(e) => setKeywordsValue(e.target.value)}
                onBlur={handleInputBlur}
                onKeyPress={handleKeyPress}
                placeholder="(Optional) Also add Keywords for the usecase/feature to help the model search the codebase (separated by a comma)"
                rows={3}
                style={{
                  ...textareaStyle,
                  marginRight: "55px", // To align with the input above (accounting for Go button width)
                }}
                className="custom-usecase-keywords-input"
                disabled={isGenerating}
              />
            )}
          </div>
        </div>

        {isExpanded && (
          <p
            className="SourceDocFileInternalTextSub"
            style={{
              whiteSpace: "pre-wrap",
              color: theme.custom.darkerGrey,
              marginTop: 8,
              marginLeft: 32,
              paddingRight: "15px",
              fontSize: "12px",
            }}
          >
            {`Describe the usecase/feature in ${
              currentRepo || "the repository"
            } that you would like to understand how it works.`}
          </p>
        )}
      </div>
    </div>
  );
};

export default CustomUsecaseItem;
